import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import menuService, { MenuServiceGetAllFilterPayload } from 'features/MenuBook/menuService';
import { SelectMenu } from 'features/MenuBook/models';
import { MenuCourse } from 'features/MenuBook/models/course';
import { COUPON_CLASSIFICATIONS } from 'features/MenuList/constants';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { CouponClassificationMap } from 'features/common/models';
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { displayError } from 'utils';
import { handleServiceError } from 'utils/axiosError';
import './style.scss';

export interface SelectCourseMenuModalProps {
  selectedIds?: number[];
  couponClassificationSource?: CouponClassificationMap;
  onClose?: () => void;
  onReflect?: (selectedMenuIds: MenuCourse[]) => void;
  disabledIds?: number[];
}

function SelectCourseMenuModal({
  selectedIds = [],
  couponClassificationSource,
  onClose,
  onReflect,
  disabledIds = [],
}: SelectCourseMenuModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // states
  const [menuList, setMenuList] = useState<SelectMenu[]>([]);
  const [filter, setFilter] = useState<MenuServiceGetAllFilterPayload>({
    selectedIds: selectedIds,
    foodSystemMenuCd: '',
    foodSystemMenuName: '',
  });

  //ref
  const tableDataRef = useRef<HTMLTableElement>(null);
  const selectedMenuListRef = useRef<SelectMenu[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const response = await menuService.getAll(filter);
        const newMenuList = response.data;

        let selectedMenuIdsRef = selectedMenuListRef.current.map((selectedMenu) => selectedMenu.id);
        const selectedMenuList = newMenuList.filter(
          (menu) => selectedIds.includes(menu.id) && !selectedMenuIdsRef.includes(menu.id)
        );
        selectedMenuListRef.current = selectedMenuListRef.current.concat(selectedMenuList);
        selectedMenuIdsRef = selectedMenuListRef.current.map((selectedMenu) => selectedMenu.id);

        const filteredMenuList = newMenuList.filter((menu) => !selectedMenuIdsRef.includes(menu.id));
        const renderMenuList = selectedMenuListRef.current.concat(filteredMenuList);
        setMenuList(renderMenuList);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  function handleClose() {
    onClose && onClose();
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const cd = form['food_sytem_menu_cd'].value.trim();
    const name = form['food_sytem_menu_name'].value.trim();

    const newFilter: MenuServiceGetAllFilterPayload = {
      ...filter,
      foodSystemMenuCd: cd,
      foodSystemMenuName: name,
    };
    setFilter(newFilter);
  }

  function handleSelection() {
    const chkSelectedMenuElements = document.querySelectorAll<HTMLInputElement>('.chk-selected-menu > input:checked');

    const selectedMenuCourses: MenuCourse[] = [];
    chkSelectedMenuElements.forEach((input) => {
      const menuId = Number(input.value);
      const selectedMenu = menuList.find((menu) => menu.id === menuId);
      if (selectedMenu) {
        selectedMenuCourses.push({
          menu_id: selectedMenu.id,
          coupon_classification: selectedMenu.coupon_classification,
          cd: selectedMenu.food_system_menu.cd,
          name: selectedMenu.food_system_menu.name,
          is_auto_add: disabledIds.includes(menuId),
        });
      }
    });

    onReflect && onReflect(selectedMenuCourses);
  }

  function getShortNameClassification(couponClassification: number) {
    return COUPON_CLASSIFICATIONS[couponClassification] ?? '';
  }

  function getFullNameClassification(couponClassification: number) {
    let name: string = '';
    if (couponClassificationSource) {
      name = couponClassificationSource[couponClassification] ?? '';
    }

    if (name.length === 0) return '';

    return '(' + name + ')';
  }

  function handleSelectMenu(event: ChangeEvent<HTMLInputElement>, menu: SelectMenu) {
    const checked = event.target.checked;
    const currentSelectedMenuList = [...selectedMenuListRef.current];
    if (checked) {
      currentSelectedMenuList.push(menu);
    } else {
      currentSelectedMenuList.splice(currentSelectedMenuList.indexOf(menu), 1);
    }
    selectedMenuListRef.current = currentSelectedMenuList;
  }

  return (
    <Modal size="lg" dialogClassName="select-course-menu-modal" show backdrop="static" onHide={handleClose} centered>
      <Modal.Header closeButton className={`${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <FeatureTitleBar title={t('SelectCourseMenuModal.head_title')} />
      </Modal.Header>
      <Modal.Body className={`${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <form className="mb-3 border p-3" onSubmit={handleSubmit}>
          <Row>
            <Col sm={9}>
              <Row className="mb-3">
                <Col sm={6} className="text-end">
                  <Form.Label htmlFor="txt-menu-code" className="col-form-label">
                    {t('SelectCourseMenuModal.menu_code')}：
                  </Form.Label>
                </Col>
                <Col sm={6}>
                  <Form.Control id="txt-menu-code" name="food_sytem_menu_cd" />
                </Col>
              </Row>
              <Row>
                <Col sm={6} className="text-end">
                  <Form.Label htmlFor="txt-menu-name" className="col-form-label">
                    {t('SelectCourseMenuModal.menu_name')}：
                  </Form.Label>
                </Col>
                <Col sm={6}>
                  <Form.Control id="txt-menu-name" name="food_sytem_menu_name" />
                </Col>
              </Row>
            </Col>
            <Col sm={3} className="d-flex align-items-end justify-content-end">
              <Button type="submit">{t('SelectCourseMenuModal.search')}</Button>
            </Col>
          </Row>
        </form>
        <div className="px-3 text-end">
          <Button className="btn--dark-green" onClick={handleSelection}>
            {t('SelectCourseMenuModal.selection')}
          </Button>
        </div>
        <div className="sticky-table flex-grow-1 flex-shrink-1 mt-2" style={{ maxHeight: '320px' }}>
          <Table ref={tableDataRef} hover>
            <thead>
              <tr>
                <th style={{ width: '10%' }}>{t('SelectCourseMenuModal.selection')}</th>
                <th style={{ width: '30%' }}>{t('SelectCourseMenuModal.menu_code')}</th>
                <th style={{ width: '60%' }}>{t('SelectCourseMenuModal.menu')}</th>
              </tr>
            </thead>
            <tbody>
              {menuList.map(function (menu) {
                const shortNameCouponClassificaiton = getShortNameClassification(menu.coupon_classification);
                const fullNameCouponClassificaiton = getFullNameClassification(menu.coupon_classification);

                return (
                  <tr key={menu.id}>
                    <td className="text-center">
                      <Form.Check
                        defaultChecked={menu.selected}
                        value={menu.id}
                        className="chk-selected-menu custom-checkbox"
                        disabled={disabledIds.includes(menu.id)}
                        onChange={(e) => handleSelectMenu(e, menu)}
                      ></Form.Check>
                    </td>
                    <td className="text-center">{menu.food_system_menu.cd + shortNameCouponClassificaiton}</td>
                    <td className="text-center">
                      {menu.food_system_menu.name}
                      {couponClassificationSource && ` ${fullNameCouponClassificaiton}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default SelectCourseMenuModal;
